import classnames from 'classnames'
import React from 'react'
import { CTALink } from '../../elements/CTALink'
import { FeatureGridItem } from '../../elements/FeatureGridItem'
import { Text } from '../../elements/Text'
import { LegacyColor, containerMaxWidth, getColorTheme, gridGap, mq } from '../../utils'
import { IImageLayout, ILayout, IProps } from './index'
import { Image } from '../../elements/Image'
import { useMediaQuery } from 'marketing-site/src/library/utils/mediaQuery'
import { BrandOptions, useBrand } from '../../providers/brandProvider'
import { FeatureGrid as FeatureGridDefault } from './component'

// type guard
function isImageLayout(data: IImageLayout | ILayout): data is IImageLayout {
  return !!(data as IImageLayout).imageRef
}

export const FeatureGrid = (props: IProps) => {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <FeatureGridDefault {...props} />
  }
  return <FeatureGridLegacy {...props} />
}

const FeatureGridLegacy = (props: IProps) => {
  const {
    items,
    title,
    cta,
    layout,
    description,
    leftAlignImage,
    bgColor = LegacyColor.White,
  } = props

  const themeColors = getColorTheme('legacy', bgColor)
  const backgroundClasses = classnames('feature-grid-background', {
    'feature-grid-background--inset-block': !(
      themeColors.backgroundColor === LegacyColor.Transparent ||
      themeColors.backgroundColor === LegacyColor.White
    ),
  })

  const isMobile = useMediaQuery(`(${mq.mobile})`)
  if (isMobile && cta) {
    cta.textSize = 'caption+'
  }

  const oddTotal = items.length % 2 === 1
  const featureGridClasses = classnames({
    'feature-grid': true,
    'one-column': !isImageLayout(layout),
    'two-columns': isImageLayout(layout),
  })
  const contentClasses = classnames({
    'center-align': !isImageLayout(layout) && layout.centerAlign,
    'center-heading': !isImageLayout(layout),
    content: true,
    'left-align': !layout.centerAlign,
  })

  return (
    <div className={backgroundClasses}>
      <div className={featureGridClasses}>
        {/* Image */}
        {isImageLayout(layout) && (
          <div className="image">
            <Image {...layout.imageRef} />
          </div>
        )}
        <div className={contentClasses}>
          {/* Title */}
          <div className="title">
            <Text size="xxl+">{title}</Text>
          </div>

          {description ? (
            <div className="feature-grid__description">
              <Text size="lg">{description}</Text>
            </div>
          ) : null}

          {/* Items */}
          <ul className="items">
            {items.map((item, index) => {
              return (
                <li key={index} className="list-item">
                  <FeatureGridItem {...item} />
                </li>
              )
            })}
          </ul>
          {/* CTA */}
          {cta && (
            <div className="cta">
              <CTALink {...cta} arrow />
            </div>
          )}
        </div>
      </div>
      <style jsx>
        {`
          .feature-grid-background {
            background-color: ${themeColors.backgroundColor};

            &--inset-block {
              padding: 4em 0;
            }
          }

          .feature-grid {
            align-content: center;
            display: grid;
            grid-template-columns: [left-gutter] 1fr [content] 12fr [right-gutter] 1fr;
            justify-items: center;
            margin: 0 auto;
            max-width: ${containerMaxWidth};

            &__description {
              margin: 3rem 0;
              max-width: 40em;
              text-align: justify;
            }
          }
          .center-align {
            justify-items: center;
            justify-self: center;
            text-align: center;
          }
          .center-heading .title {
            text-align: center;
          }
          .left-align {
            justify-items: left;
            justify-self: left;
            text-align: left;
          }
          .content {
            grid-column: content;
          }
          .title {
            margin-bottom: 35px;
          }
          .list-item {
            margin-bottom: 20px;
            max-width: 400px;
          }
          .image {
            grid-column: content;
            max-height: 500px;
            max-width: 100%;
            margin-bottom: 60px;
            border-radius: 25px;
            overflow: hidden;
          }
          .cta {
            margin: ${layout.centerAlign ? '0 auto' : '0'};
            margin-top: 36px;
            max-width: 400px;
          }
          @media (${mq.tablet}) {
            .feature-grid {
              padding: 0 40px;
            }
            .items {
              display: grid;
              grid-template-columns: 1fr 1fr;
              grid-column-gap: ${gridGap.desktop};
            }
            .list-item {
              margin-bottom: 36px;
            }
            .list-item:last-child {
              grid-column: ${oddTotal && layout.centerAlign ? '1 / span 2' : 'auto'};
              margin: ${oddTotal && layout.centerAlign ? '0 auto' : 0};
            }
          }
          @media (${mq.desktop}) {
            .one-column {
              grid-template-columns: ${layout.centerAlign
                ? '[left-gutter] 3fr [content] 8fr [right-gutter] 3fr'
                : '[left-gutter] 2fr [content] 10fr [right-gutter] 2fr'};
            }
            .two-columns {
              grid-template-columns: ${leftAlignImage
                ? '[image] 5fr [gap] 1fr [content] 7fr'
                : '[content] 7fr [gap] 1fr [image] 5fr'};
            }
            .content {
              grid-row: 1 / span 2;
              grid-column: content;
            }
            .title {
              margin-bottom: 48px;
            }
            .image {
              grid-column: image;
              grid-row: 1 / span 2;
              margin: auto;
              max-height: unset;
            }

            .feature-grid-background {
              &--inset-block {
                padding: 7.3em 0;
              }
            }
          }
        `}
      </style>
    </div>
  )
}
